<script setup>
// 섹션 배경이 스크롤에 따라 서로 겹쳐 바뀌는 층. 디자이너가 말한 "디졸브"다 —
// 피그마 프로토타입 전환의 Dissolve, 프레임 하나가 다른 프레임으로 통째로 겹쳐 바뀌는 것.
//
// 왜 이렇게 하나
//   경계에 그라데이션 띠를 깔면 색이 바뀌는 자리가 페이지에 못박혀서,
//   스크롤할 때 띠가 화면을 가로질러 지나간다. 그건 디졸브가 아니다.
//   배경을 화면에 붙박아 두고(fixed) 색만 섞으면 화면 전체가 동시에 물들어
//   경계가 어디인지 눈에 보이지 않는다.
//
// 층을 통째로 겹쳐 두고 opacity 만 바꾼다. 그래서 단색이든 그라데이션이든 똑같이 섞인다 —
// 앱 섹션 배경이 그라데이션이라 색 하나로는 줄일 수가 없다.
//
// mix 는 "지금 몇 번째 배경에 있는가"를 소수로 나타낸 값이다.
// 1 이면 1번만 보이고, 1.5 면 1번과 2번이 반반이다. 계산은 HomeSceneView 가 한다.
//
// 배경은 되감는다. 연출이 아니라 "지금 어디에 있는가"를 나타내는 상태라서,
// 위로 올리면 그 자리 색으로 돌아와야 한다. (연출 쪽은 되감지 않는다 — 규칙 3번)
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps({
  // [{ key, css, art, deco }] — css 는 background 값 그대로다. 단색도 그라데이션도 된다.
  //
  // art 와 deco 는 그 배경에 같이 까는 장식이다(2026-09-26). 섹션 안에 두면
  // 콘텐츠 높이에 잘리고, 스크롤할 때 그 잘린 직선이 화면을 가로질러 지나간다.
  // 배경은 배경대로 둬야 해서 이리로 옮겼다.
  //   art  { src, ratio, height, left, rotate } — 그림 하나. height/left 는 화면 대비 퍼센트.
  //   deco 컴포넌트. 여러 조각이거나 화면 폭에 맞춰 줄여야 할 때 쓴다.
  //        층의 폭을 width 로 받는다(비전 곡선).
  layers: { type: Array, required: true },
  mix: { type: Number, default: 0 },
  // 지금 연출 프레임. layer.decoFrame 이 있는 층은 색과 따로, 이 프레임이
  // 올라간 뒤에 장식만 fade in 한다(2026-10-16 클라이언트 요청).
  step: { type: Number, default: 0 },
  // 페이지 배율. deco 가 제 크기를 구하는 데 쓴다.
  zoom: { type: Number, default: 1 },
  // 위에서 비워 둘 높이(설계 px). 공용 상단이 시세표를 펼쳐 커진 만큼을 받는다.
  //
  // 이 층은 fixed 라 바깥 상자의 padding 을 따라가지 않는다. 그래서 헤더가 커질 때
  // 본문만 내려가고 이 배경은 그대로 남아, 펼친 표가 히어로 그림 위에 얹힌
  // "레이어"로 보였다(2026-09-29 지적). 같은 값만큼 이 층도 내려 주면
  // 히어로가 헤더 아래에서 다시 시작해 띠가 늘어난 것처럼 보인다.
  offset: { type: Number, default: 0 },
})

// deco 가 쓸 "이 층의 제 폭".
//
// 재지 않고 계산한다. 층은 늘 화면 전체이고 배율(zoom) 안에서 그려지므로
//   제 폭 = 화면 폭 / zoom
// 이다. 재려고 하면 시점이 문제가 된다 - 이 컴포넌트의 onMounted 는 부모보다 먼저
// 돌아서 그때는 zoom 이 아직 1 이고, ResizeObserver 가 안 뜨는 환경에서는 그 값이
// 그대로 굳는다(2026-09-26: 곡선이 창 폭의 80% 만 덮었다).
const viewW = ref(typeof window === 'undefined' ? 0 : document.documentElement.clientWidth)
const readWidth = () => {
  viewW.value = document.documentElement.clientWidth
}
const width = computed(() => viewW.value / (props.zoom || 1))

onMounted(() => {
  readWidth()
  window.addEventListener('resize', readWidth)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', readWidth)
})

// 이웃한 두 층만 겹친다. 합이 늘 1 이라 중간에 색이 탁해지지 않는다.
const opacityOf = (index, mix) => Math.max(0, 1 - Math.abs(mix - index))
</script>

<template>
  <div
    aria-hidden
    class="pointer-events-none fixed inset-0 z-0"
    :style="offset ? { top: `${offset}px` } : null"
  >
    <div
      v-for="(layer, i) in layers"
      :key="layer.key"
      class="absolute inset-0"
      :style="{ background: layer.css, opacity: opacityOf(i, mix) }"
    >
      <!-- 배경에 같이 까는 그림. 화면 기준이라 섹션 높이와 무관하고, 잘리는 자리가
           콘텐츠 상자 끝이 아니라 화면 끝이라 잘린 선이 보이지 않는다. -->
      <img
        v-if="layer.art"
        alt=""
        class="absolute block max-w-none"
        :src="layer.art.src"
        :style="{
          height: layer.art.height,
          aspectRatio: layer.art.ratio,
          left: layer.art.left,
          top: '50%',
          transform: `translate(-50%, -50%) rotate(${layer.art.rotate})`,
        }"
      />
      <!-- 조각이 여럿이거나 화면 폭에 맞춰 줄여야 하는 장식.
           decoFrame 이 있으면 **색과 따로** 논다 - 색은 스크롤을 따라 섞이고,
           장식은 그 프레임(= 그 섹션 글이 뜨는 시점)에 맞춰 fade in 한다.
           비전 곡선이 그렇다: 앱에서 내려올 때는 흰 바탕만 오고, 제목이 뜰 때
           곡선이 떠오른다(2026-10-16 클라이언트 요청). -->
      <div
        v-if="layer.deco"
        class="absolute inset-0"
        :style="{
          opacity: layer.decoFrame == null || step >= layer.decoFrame ? 1 : 0,
          transition: `opacity ${layer.decoFade ?? 600}ms ease`,
        }"
      >
        <component :is="layer.deco" :width="width" />
      </div>
    </div>
  </div>
</template>
